import React, {
  createContext,
  type FC,
  useCallback,
  useContext,
  useMemo,
  useState,
} from "react";
import { type ScreenNameType } from "clientState/AuthFlow/providers/ScreenProvider/contract";
import { screenNames } from "clientState/AuthFlow/providers/ScreenProvider/screenNames";

import { DEFAULT_TITLE_COLOR } from "./constants";
import type {
  AuthenticationFlows,
  AuthenticationState,
  AuthenticationStatusType,
  CancelAuthFlowFn,
  CustomContent,
  ModalActions,
  ModalState,
  StartAuthFlowFn,
} from "./contract";

const { NONE, AUTH_METHODS } = screenNames;

const AuthenticationFlowsContext = createContext<
  AuthenticationFlows | undefined
>(undefined);
const AuthenticationStateContext = createContext<
  AuthenticationState | undefined
>(undefined);
const ModalStateContext = createContext<ModalState | undefined>(undefined);
const ModalActionsContext = createContext<ModalActions | undefined>(undefined);

const ModalScreenProvider: FC = ({ children }) => {
  const [origin, setOrigin] = useState<string | undefined>(undefined);
  const [customContent, setCustomContent] = useState<CustomContent | undefined>(
    undefined,
  );
  const [selectedFlow, setSelectedFlow] = useState<ScreenNameType>(NONE);
  const [emailLinkPath, setEmailLinkPath] = useState<string | undefined>(
    undefined,
  );
  const [isCloseable, setIsCloseable] = useState(true);
  const [isInitialCloseable, setIsInitialCloseable] = useState(true);
  const [isSuccessCloseable, setIsSuccessCloseable] = useState(false);
  const [isLoginSyncWaitingCloseable, setIsLoginSyncWaitingCloseable] =
    useState<boolean>(false);
  const [customScreensConfig, setCustomScreensConfig] = useState({});
  const [skipAuthMethods, setSkipAuthMethods] = useState(false);
  const [status, setStatus] = useState<AuthenticationStatusType>("not-started");
  const [titleColor, setTitleColor] = useState<string | undefined>();
  const [hasNewsletter, setHasNewsletter] = useState<boolean>(false);
  const [hasFooterLink, setHasFooterLink] = useState<boolean>(false);
  const [email, setEmail] = useState<string>();

  const closeModal = useCallback(() => setSelectedFlow(NONE), []);

  const startFlow = useCallback<StartAuthFlowFn>(
    (
      {
        origin,
        customContent,
        customScreensConfig,
        emailLinkPath,
        isCloseable = true,
        isSuccessCloseable,
        isLoginSyncWaitingCloseable,
        skipAuthMethods,
        titleColor,
        hasNewsletter,
        hasFooterLink,
        email,
      } = { origin: "" },
    ) => {
      setOrigin(origin);
      setSelectedFlow(AUTH_METHODS);
      setCustomContent(customContent);
      setEmailLinkPath(emailLinkPath);
      setIsCloseable(isCloseable);
      setIsInitialCloseable(isCloseable);
      setIsSuccessCloseable(isSuccessCloseable ?? false);
      setIsLoginSyncWaitingCloseable(isLoginSyncWaitingCloseable ?? false);
      setCustomScreensConfig(customScreensConfig || {});
      setSkipAuthMethods(skipAuthMethods ?? false);
      setStatus("in-progress");
      setTitleColor(titleColor ? titleColor : DEFAULT_TITLE_COLOR);
      setHasNewsletter(hasNewsletter ?? false);
      setHasFooterLink(hasFooterLink ?? false);
      setEmail(email);
    },
    [],
  );

  const cancelFlow = useCallback<CancelAuthFlowFn>(() => {
    setStatus("cancelled");
    closeModal();
  }, [closeModal]);

  const preventCloseableModal = useCallback(() => {
    setIsCloseable(false);
  }, []);
  const allowCloseableModal = useCallback(() => {
    setIsCloseable(true);
  }, []);
  const resetIsClosable = useCallback(() => {
    if (isInitialCloseable) {
      allowCloseableModal();
    } else {
      preventCloseableModal();
    }
  }, [isInitialCloseable, allowCloseableModal, preventCloseableModal]);

  const updateAuthenticationStatus = useCallback(
    (newStatus: AuthenticationStatusType) => setStatus(newStatus),
    [],
  );

  const authenticationFlowsValue = useMemo(
    () => ({
      cancelFlow,
      startFlow,
    }),
    [cancelFlow, startFlow],
  );

  const authenticationStateValue = useMemo(
    () => ({ isModalOpen: selectedFlow !== NONE, status }),
    [selectedFlow, status],
  );

  const stateValue = useMemo(
    () => ({
      origin,
      customContent,
      customScreensConfig,
      emailLinkPath,
      isCloseable,
      isInitialCloseable,
      isSuccessCloseable,
      isLoginSyncWaitingCloseable,
      selectedFlow,
      skipAuthMethods,
      titleColor,
      hasNewsletter,
      hasFooterLink,
      email,
    }),
    [
      origin,
      customContent,
      customScreensConfig,
      emailLinkPath,
      isCloseable,
      isInitialCloseable,
      isSuccessCloseable,
      isLoginSyncWaitingCloseable,
      selectedFlow,
      skipAuthMethods,
      titleColor,
      hasNewsletter,
      hasFooterLink,
      email,
    ],
  );

  const actionsValue = useMemo(
    () => ({
      closeModal,
      preventCloseableModal,
      updateAuthenticationStatus,
      allowCloseableModal,
      resetIsClosable,
    }),
    [
      closeModal,
      preventCloseableModal,
      updateAuthenticationStatus,
      allowCloseableModal,
      resetIsClosable,
    ],
  );

  return (
    <AuthenticationStateContext.Provider value={authenticationStateValue}>
      <AuthenticationFlowsContext.Provider value={authenticationFlowsValue}>
        <ModalStateContext.Provider value={stateValue}>
          <ModalActionsContext.Provider value={actionsValue}>
            {children}
          </ModalActionsContext.Provider>
        </ModalStateContext.Provider>
      </AuthenticationFlowsContext.Provider>
    </AuthenticationStateContext.Provider>
  );
};

export default ModalScreenProvider;

export const useAuthenticationFlows = (): AuthenticationFlows => {
  const context = useContext(AuthenticationFlowsContext);

  if (context === undefined) {
    throw new Error("useAuthenticationFlows was used outside of its provider");
  }

  return context;
};

export const useAuthenticationState = (): AuthenticationState => {
  const context = useContext(AuthenticationStateContext);

  if (context === undefined) {
    throw new Error("useAuthenticationState was used outside of its provider");
  }

  return context;
};

export const useModalState = (): ModalState => {
  const context = useContext(ModalStateContext);

  if (context === undefined) {
    throw new Error("useModalState was used outside of its provider");
  }

  return context;
};

export const useModalActions = (): ModalActions => {
  const context = useContext(ModalActionsContext);

  if (context === undefined) {
    throw new Error("useModalActions was used outside of its provider");
  }

  return context;
};
